import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AdminAssistGroundedSuggestion from './AdminAssistGroundedSuggestion';
import AdminAssistInvestorResponses from './AdminAssistInvestorResponses';
import { isFollowUpBusyForEntry, isFollowUpRegenerating } from './chatAiAssistDrawerUtils';
import { ChatAiAssistState } from '../../hooks/admin/useChatAiAssist';
import { AdminChatAssistFollowUp } from '../../models/admin/AdminChatAssistResult';
import { ChatLinkTarget } from '../../utils/chatLinkCorpus';

interface ChatAiAssistFollowUpItemProps {
  followUp: AdminChatAssistFollowUp;
  conversationId: string | null;
  corpus: ChatLinkTarget[] | null;
  targetMessageId: string;
  assist: ChatAiAssistState;
  onUseInvestorResponse?: (text: string) => void;
}

export const ChatAiAssistFollowUpItem: React.FC<ChatAiAssistFollowUpItemProps> = ({
  followUp,
  conversationId,
  corpus,
  targetMessageId,
  assist,
  onUseInvestorResponse,
}) => (
  <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px dashed #e3e8ef' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Your question
      </Typography>
      <Tooltip title="Regenerate this follow-up response">
        <span>
          <IconButton
            size="small"
            aria-label="Regenerate follow-up"
            onClick={(event) => {
              event.stopPropagation();
              void assist.regenerateFollowUp(targetMessageId, followUp.id, followUp.adminQuestion);
            }}
            disabled={isFollowUpBusyForEntry(assist.followUpLoadingId, targetMessageId)}
          >
            {isFollowUpRegenerating(assist.followUpLoadingId, targetMessageId, followUp.id) ? (
              <CircularProgress size={16} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
      {followUp.adminQuestion}
    </Typography>
    <AdminAssistGroundedSuggestion
      messageId={`ai-assist-follow-up-${conversationId ?? 'none'}-${followUp.id}`}
      text={followUp.suggestion}
      corpus={corpus}
      grounding={followUp.googleSearchGrounding}
      markdownVariant="block"
    />
    {onUseInvestorResponse && (
      <AdminAssistInvestorResponses
        responses={followUp.investorResponses ?? []}
        onSelect={onUseInvestorResponse}
      />
    )}
  </Box>
);

interface ChatAiAssistFollowUpComposerProps {
  targetMessageId: string;
  assist: ChatAiAssistState;
}

export const ChatAiAssistFollowUpComposer: React.FC<ChatAiAssistFollowUpComposerProps> = ({
  targetMessageId,
  assist,
}) => {
  const followUpBusy = isFollowUpBusyForEntry(assist.followUpLoadingId, targetMessageId);

  return (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid #eef2f7' }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          size="small"
          placeholder="Ask a follow-up…"
          value={assist.followUpDraft}
          onChange={(event) => assist.setFollowUpDraft(event.target.value)}
          disabled={followUpBusy}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (assist.followUpDraft.trim()) {
                void assist.submitFollowUp(targetMessageId, assist.followUpDraft);
              }
            }
          }}
        />
        <IconButton
          size="small"
          color="primary"
          aria-label="Ask follow-up"
          disabled={!assist.followUpDraft.trim() || followUpBusy}
          onClick={(event) => {
            event.stopPropagation();
            void assist.submitFollowUp(targetMessageId, assist.followUpDraft);
          }}
          sx={{
            mb: 0.25,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {assist.followUpLoadingId === targetMessageId ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      {assist.followUpError && assist.expandedTargetId === targetMessageId && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>
          {assist.followUpError}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        Enter to send · Shift+Enter for new line
      </Typography>
    </Box>
  );
};
